import React, { createContext, useContext, useState, useCallback } from "react";
import { ToastContainer, ToastData, ToastType } from "../components/Toast";

interface NotificationContextType {
    showNotification: (message: string, type?: ToastType, duration?: number) => void;
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }
    return context;
};

interface NotificationProviderProps {
    children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastData[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const showNotification = useCallback((message: string, type: ToastType = "info", duration?: number) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast: ToastData = {
            id,
            message,
            type,
            duration,
        };
        setToasts((prev) => [...prev, newToast]);
    }, []);

    const success = useCallback((message: string, duration?: number) => {
        showNotification(message, "success", duration);
    }, [showNotification]);

    const error = useCallback((message: string, duration?: number) => {
        showNotification(message, "error", duration);
    }, [showNotification]);

    const warning = useCallback((message: string, duration?: number) => {
        showNotification(message, "warning", duration);
    }, [showNotification]);

    const info = useCallback((message: string, duration?: number) => {
        showNotification(message, "info", duration);
    }, [showNotification]);

    const value: NotificationContextType = {
        showNotification,
        success,
        error,
        warning,
        info,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
